import { ru } from 'date-fns/locale'
import {
  format,
  parseISO,
  isToday,
  isTomorrow,
  isSameDay,
} from 'date-fns'
import { getFirstLetterUpper } from 'www/utils'
import { DAY_TIMES, WEEK_DATES } from 'www/vue_components/common/TheFilters/doctors/constants'
import type { Filters } from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore.types'

function getDateLabel({ dateRange, timesOfDay }: {
  dateRange: Filters['dateRange']
  timesOfDay: Filters['timesOfDay']
}) {
  /**
   * Форматирует выбранные времена дня в читаемую строку.
   * Например: "утро и вечер" или "утро, день и вечер".
   */
  const formattedTimes = () => {
    if (!timesOfDay) {
      return ''
    }

    return timesOfDay
      .map(key => DAY_TIMES[key].name.toLowerCase()) // Преобразуем ключи в название
      .join(', ') // Объединяем времена через запятую
      .replace(/,([^,]*)$/, ' и$1') // Последнюю запятую меняем на букву "и"
  }

  /**
   * Форматирует выбранный диапазон дат в читаемую строку.
   * Например: "5 окт" или "5-7 окт".
   */
  const formattedDateRange = () => {
    if (!dateRange) {
      return ''
    }

    // Парсим даты
    const [startDate, endDate] = dateRange.map(date => parseISO(date))

    // Форматируем даты
    const formatDate = (date: Date) => ({
      day: format(date, 'd', { locale: ru }),
      month: format(date, 'MMMM', { locale: ru }).substring(0, 3),
    })

    const start = formatDate(startDate)
    const end = formatDate(endDate || startDate)

    // Если даты в одном месяце
    if (start.month === end.month) {
      if (start.day === end.day) {
        if (isToday(startDate)) {
          return 'Сегодня'
        }

        if (isTomorrow(startDate)) {
          return 'Завтра'
        }

        return `${start.day} ${start.month}` // Одна дата "5 окт"
      }

      if (isSameDay(startDate, WEEK_DATES.saturday) && isSameDay(endDate, WEEK_DATES.sunday)) {
        return 'На выходных'
      }

      return `${start.day}-${end.day} ${start.month}` // Диапазон дат "5-7 окт"
    }

    // Если даты в разных месяцах "5 сен – 7 окт"
    return `${start.day} ${start.month} – ${end.day} ${end.month}`
  }

  /**
   * Формирует итоговое название на основе выбранных даты и времени.
   */
  const generateLabel = () => {
    const dateText = formattedDateRange()
    const timeText = formattedTimes()

    // Если ничего не выбрано
    if (!dateText && !timeText) {
      return 'Дата и время'
    }

    // Если выбраны все три времени, считаем это полным днём и отображаем только дату
    if (timesOfDay?.length === 3 && dateText) {
      return dateText
    }

    return getFirstLetterUpper(
      [dateText, timeText].filter(Boolean).join(', '),
    )
  }

  return generateLabel()
}

export default getDateLabel
